"use client";

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { createClient } from "@/lib/supabase/client";
import type { ReferralRewardsResponse } from "@/app/api/referrals/rewards/route";

// Mounted globally in app/layout.tsx. On first load for an authenticated
// user, fetches unseen referral rewards and toasts them, then POSTs to mark
// them as seen so the same events don't toast on the next page load.
//
// Skipped automatically on /referrals — that page handles its own visit-mark
// flow and showing a toast there would be redundant with the inline history.
export function ReferralRewardToaster() {
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (typeof window !== "undefined" && window.location.pathname.startsWith("/referrals")) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase.auth.getUser();
        if (cancelled || !data.user) return;

        const res = await fetch("/api/referrals/rewards", { cache: "no-store" });
        if (cancelled || !res.ok) return;

        const payload: ReferralRewardsResponse = await res.json();
        const unseen = payload.rewards.filter((r) => r.seen_at === null);
        if (unseen.length === 0) return;

        const totalCredits = unseen.reduce((sum, r) => sum + r.credits, 0);
        const description =
          unseen.length === 1
            ? unseen[0].event_type === "paid"
              ? "A friend you referred just upgraded to a paid plan."
              : "A friend you referred just built their first project."
            : `From ${unseen.length} referral events.`;

        toast.success(`+${totalCredits} credits earned from referrals`, {
          description,
          action: {
            label: "View",
            onClick: () => {
              window.location.href = "/referrals";
            },
          },
          duration: 8000,
        });

        // Mark as seen so we don't re-toast next load. Best-effort.
        fetch("/api/referrals/rewards", { method: "POST" }).catch(() => {});
      } catch {
        // Silent — toast is non-critical
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}
